'use client';

import { cn } from '@ui/lib/utils';
import { PencilIcon } from 'lucide-react';
import React from 'react';

export interface ReadOnlyRichTextProps {
  content?: string;
  placeholder: string;
  onClick: () => void;
  className?: string;
  minHeight?: string;
  showEditIcon?: boolean;
}

export const ReadOnlyRichText = React.forwardRef<
  HTMLDivElement,
  ReadOnlyRichTextProps
>(
  (
    {
      content = '',
      placeholder,
      onClick,
      className,
      minHeight = '80px',
      showEditIcon = true,
    },
    ref
  ) => {
    const isEmpty = !content || content === '<p></p>' || content.trim() === '';

    return (
      <div
        ref={ref}
        className={cn(
          'relative border border-slate-200/50 rounded-lg shadow-sm cursor-pointer transition-all duration-200',
          'hover:border-slate-300 hover:shadow-md hover:bg-slate-50/30',
          'focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-300',
          'group',
          className
        )}
        style={{ minHeight }}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={
          isEmpty
            ? `Click to add ${placeholder.toLowerCase()}`
            : `Click to edit notes`
        }
      >
        <div
          className="p-2 sm:p-3 relative h-full"
          style={{
            minHeight,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Edit icon - appears on hover */}
          {showEditIcon && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
              <div className="bg-white rounded-lg p-1.5 shadow-sm border border-slate-200">
                <PencilIcon className="h-3.5 w-3.5 text-slate-700" />
              </div>
            </div>
          )}

          {isEmpty ? (
            // Empty state placeholder
            <div className="flex items-center justify-center h-full text-center py-4">
              <div className="text-slate-600 italic">
                <div className="text-sm font-medium">{placeholder}</div>
                <div className="text-xs mt-1 opacity-75">
                  Click to add notes
                </div>
              </div>
            </div>
          ) : (
            // Rich text content
            <div
              className="prose prose-slate max-w-none text-sm prose-headings:font-bold prose-headings:text-slate-800 prose-p:text-slate-700 prose-ul:text-slate-700 prose-ol:text-slate-700 prose-li:text-slate-700 prose-strong:text-slate-800 prose-em:text-slate-700"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}

          {/* Click overlay for better accessibility */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-blue-600/90 text-white text-xs font-medium px-2 py-1 rounded-lg shadow-lg">
              Click to edit
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ReadOnlyRichText.displayName = 'ReadOnlyRichText';
